import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { alpha, useTheme } from '@mui/material/styles';
import { useContext, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import CloseIcon from '@mui/icons-material/Close';
// hooks
import { useBoolean } from '../../hooks/use-boolean';
import { useRouter } from '../../routes/hooks';
import { useResponsive } from '../../hooks/use-responsive';
// components
import Iconify from '../../components/iconify';
import FormProvider, { RHFTextField } from '../../components/hook-form';
import { Helmet } from 'react-helmet-async';
import Logo from '../../components/logo';
import { bgGradient } from '../../theme/css';
import { Box, Link } from '@mui/material';
import { Link as RouterLink } from "react-router-dom";
import { ILoginForm } from '../../types/auth';
import { LoginService } from '../../services/auth';
import { UserContext } from '../../context/user';

const LoginPage = () => {

    // hooks
    const theme = useTheme();
    const mdUp = useResponsive('up', 'md');
    const password = useBoolean();
    const router = useRouter();

    // states
    const [errorMessage, setErrorMessage] = useState<string>('');

    // context
    const { setUser } = useContext(UserContext);

    // rhf
    const LoginSchema = Yup.object().shape({
        username: Yup.string().required('Informe o nome de usuário!'),
        password: Yup.string().required('Informe a senha'),
    });

    const defaultValues = {
        username: '',
        password: '',
    };

    const methods = useForm({
        resolver: yupResolver<ILoginForm>(LoginSchema),
        defaultValues,
    });

    const {
        handleSubmit,
        formState: { isSubmitting },
    } = methods;

    const onSubmit = handleSubmit(async (data: ILoginForm) => {
        await LoginService(data).then(resp => {
            setUser(resp.data.user);           
            
            if(resp.data.user.systemBranch.length <= 0)
            {
                setErrorMessage("Você não possui acesso a nenhum sistema disponível!");    
                return;
            }
            
            localStorage.setItem('token', resp.data.token);
            router.push('/select-system');
        }).catch(error => {
            setErrorMessage(error?.response?.data?.message || 'Ocorreu um erro');
        });
    });

    const renderSection = (
        <Stack
            flexGrow={1}
            spacing={10}
            alignItems="center"
            justifyContent="center"
            sx={{
                ...bgGradient({
                    color: alpha(
                        theme.palette.background.default,
                        theme.palette.mode === 'light' ? 0.88 : 0.94
                    ),
                    imgUrl: '/assets/background/overlay_2.jpg',
                }),
            }}
        >
            <Typography variant="h3" sx={{ maxWidth: 480, textAlign: 'center' }}>
                Olá, Bem-Vindo(a) de volta
            </Typography>

            <Box
                component="img"
                alt="auth"
                src={'../../assets/illustrations/illustration_dashboard.png'}
                sx={{
                    maxWidth: {
                        xs: 480,
                        lg: 560,
                        xl: 720,
                    },
                }}
            />
        </Stack>
    );

    return (
        <>
            <Stack
                component="main"
                direction="row"
                sx={{
                    minHeight: '100vh',
                }}
            >
                <Logo
                    sx={{
                        zIndex: 9,
                        position: 'absolute',
                        m: { xs: 2, md: 5 },
                    }}
                />

                {mdUp && renderSection}

                <Stack
                    sx={{
                        width: 1,
                        mx: 'auto',
                        maxWidth: 480,
                        px: { xs: 2, md: 8 },
                        pt: { xs: 15, md: 20 },
                        pb: { xs: 15, md: 0 },
                    }}
                >
                    <Helmet>
                        <title>Gerenciamento Cenix</title>
                    </Helmet>
                    <FormProvider methods={methods} onSubmit={onSubmit}>
                        <Stack spacing={2} sx={{ mb: 5 }}>
                            <Typography variant="h4">Gerenciamento Cenix</Typography>

                        </Stack>
                        <Stack spacing={2.5} sx={{ mt: 4 }}>
                            {!!errorMessage && <Alert
                                severity="error"
                                action={
                                    <IconButton
                                      aria-label="close"
                                      color="inherit"
                                      size="small"
                                      onClick={() => {
                                        setErrorMessage("");
                                      }}
                                    >
                                      <CloseIcon fontSize="inherit" />
                                    </IconButton>
                                  }
                            >
                                {errorMessage}
                            </Alert>}

                            <RHFTextField name="username" label="Nome de usuário" autoFocus />

                            <RHFTextField
                                name="password"
                                label="Senha"
                                type={password.value ? 'text' : 'password'}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={password.onToggle} edge="end">
                                                <Iconify icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Link component={RouterLink} to={"/forgot-password"} variant="body2" color="inherit" underline="always" sx={{ alignSelf: 'flex-end' }}>
                                Esqueceu sua senha?
                            </Link>

                            <LoadingButton
                                fullWidth
                                color="inherit"
                                size="large"
                                type="submit"
                                variant="contained"
                                loading={isSubmitting}
                                sx={{ mt: 4 }}
                            >
                                Entrar
                            </LoadingButton>
                        </Stack>
                    </FormProvider>
                </Stack>
            </Stack>
        </>
    );

}

export default LoginPage;